// Implements: F-UIX-008, F-UIX-010
/**
 * The `auth` module's client, on top of the single HTTP exit.
 *
 * Access is ONE request. There is no `/auth/login/totp` and no half
 * authenticated session to resume: when the account has a second factor the
 * backend answers `401` and the browser sends email, password AND `totp_code`
 * again in the same `POST /auth/login` (design section 5). That is why the
 * login form keeps the password in its own state instead of forgetting it
 * after the first refusal.
 *
 * The optional keys are OMITTED rather than sent as `null`: the phase-2
 * contract declares them optional, and a `null` where a string is expected is
 * a `422` waiting to happen.
 */

import { ApiError, UNKNOWN_ERROR_CODE } from "../../../shared/api/ApiError";
import { API_BASE } from "../../../shared/api/apiBase";
import { httpRequest } from "../../../shared/api/httpClient";
import type { SessionSummary, UserSummary } from "../../../types/api";

/** Annex §11.1: the routes of the `auth` module. */
export const LOGIN_PATH = `${API_BASE}/auth/login`;
export const LOGOUT_PATH = `${API_BASE}/auth/logout`;
export const SESSIONS_PATH = `${API_BASE}/auth/sessions`;

/**
 * The path of one session, which is the only thing `DELETE` addresses.
 *
 * The identifier is escaped even though the backend issues UUIDs: the value
 * arrives from a response body, and a client that trusts a body to be
 * URL-shaped is a client that will one day emit a path it did not mean to.
 */
export function sessionPath(sessionId: string): string {
  return `${SESSIONS_PATH}/${encodeURIComponent(sessionId)}`;
}

/** What the login screen collects, in the vocabulary of the screen. */
export interface LoginCredentials {
  readonly email: string;
  readonly password: string;
  /** The six-digit code, once the account revealed that it wants one. */
  readonly totpCode?: string | undefined;
  /** A name for the session, for the device list of slice 3b. */
  readonly deviceLabel?: string | undefined;
}

/** The wire body, in the vocabulary of the backend (snake_case, annex §11.1). */
interface LoginRequestBody {
  readonly email: string;
  readonly password: string;
  readonly totp_code?: string;
  readonly device_label?: string;
}

/** An empty string is not a code the user typed: it is a field nobody filled. */
function present(value: string | undefined): value is string {
  return value !== undefined && value !== "";
}

/** The JSON body of one login attempt. */
export function loginBody(credentials: LoginCredentials): LoginRequestBody {
  return {
    email: credentials.email,
    password: credentials.password,
    ...(present(credentials.totpCode) ? { totp_code: credentials.totpCode } : {}),
    ...(present(credentials.deviceLabel) ? { device_label: credentials.deviceLabel } : {}),
  };
}

/**
 * Sign in with one request.
 *
 * @param credentials Email, password and, on the second attempt, the code.
 * @returns The user the backend recognised.
 * @throws {ApiError} `401` for a refusal or a second factor, `429` for a
 *   closed attempt bucket, `422` for an invalid body. Which of those means
 *   what is decided by `useLogin`, not here: this file speaks HTTP.
 */
export async function login(credentials: LoginCredentials): Promise<UserSummary> {
  // `| null` because a body of literal `null` IS valid JSON: `httpRequest`
  // parses it and hands it over, and a type that did not say so would let a
  // `null` through as a `UserSummary` nobody ever checked.
  // Sent once (`retry: false`, L-040): a login opens a session and spends an attempt of the
  // rate-limit bucket, so a retry after a lost response could do either twice.
  const user = await httpRequest<UserSummary | null>(LOGIN_PATH, {
    method: "POST",
    body: loginBody(credentials),
    retry: false,
  });

  if (user === undefined || user === null) {
    // A `200` with no user is a contract violation, not a signed-in user, and
    // it must not travel one more layer as `undefined` (design section 4).
    throw new ApiError(200, UNKNOWN_ERROR_CODE, "Login answered 200 with no user", new Headers());
  }
  return user;
}

/**
 * End the session on the server.
 *
 * `204` and no body. What happens to the cache and to the address bar is
 * `useLogout`'s decision, not this file's: here there is only HTTP.
 *
 * @throws {ApiError} `401` when there was no session to end.
 */
export async function logout(): Promise<void> {
  // Sent once (`retry: false`, L-040): a POST that changes server state is never repeated.
  await httpRequest<unknown>(LOGOUT_PATH, { method: "POST", retry: false });
}

/** The wrapping object `GET /auth/sessions` answers with (annex §11.1). */
interface SessionListResponse {
  readonly sessions: readonly SessionSummary[];
}

/**
 * The sessions of the signed-in user.
 *
 * The response is an OBJECT with a `sessions` key, never a bare array. The
 * distinction is not cosmetic: an envelope can grow a field, and a top-level
 * array cannot grow anything without breaking every reader.
 *
 * @returns The rows, or none when the body carries no list.
 * @throws {ApiError} `401` when the session is gone, which the interceptor
 *   turns into a redirect (design DD6).
 */
export async function fetchSessions(): Promise<readonly SessionSummary[]> {
  const body = await httpRequest<SessionListResponse>(SESSIONS_PATH);
  return body?.sessions ?? [];
}

/**
 * Revoke one session.
 *
 * @param sessionId The identifier of the row being revoked.
 * @throws {ApiError} `403` for a session the caller may not touch and `404`
 *   for one that belongs to somebody else.
 */
export async function revokeSession(sessionId: string): Promise<void> {
  await httpRequest<unknown>(sessionPath(sessionId), { method: "DELETE" });
}

/** P2: the public recovery routes (`router_auth.py`). */
export const RESET_REQUEST_PATH = `${API_BASE}/auth/password-reset/request`;
export const RESET_CONFIRM_PATH = `${API_BASE}/auth/password-reset/confirm`;

/**
 * `POST /auth/password-reset/request`: always `202` for any address (no enumeration). Sent once
 * (`retry: false`): every request charges the login's bucket and may send one more e-mail.
 */
export async function requestPasswordReset(email: string): Promise<void> {
  await httpRequest<unknown>(RESET_REQUEST_PATH, { method: "POST", body: { email }, retry: false });
}

/**
 * `POST /auth/password-reset/confirm`: `204` and no cookie. Sent once (`retry: false`): the token
 * is spent by the first request the server accepts.
 */
export async function confirmPasswordReset(token: string, newPassword: string): Promise<void> {
  await httpRequest<unknown>(RESET_CONFIRM_PATH, {
    method: "POST",
    body: { token, new_password: newPassword },
    retry: false,
  });
}
